import { useAtomValue, useSetAtom } from 'jotai';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { useEffect, useEffectEvent, useRef } from 'react';
import { useMediaQuery } from '@/core/ui';
import { useMap } from '@/features/map';
import { WIND_COLOR_STOPS } from '@/features/stations';
import type { ViewState } from '../model/viewPlan';
import { cancelWindAtom, loadWindAtom } from '../state/actions';
import { windFieldAtom } from '../state/atoms';
import { WIND_ANCHOR, WIND_GRADIENT, WIND_PARTICLES } from './forecastStyle';
import { WindGradient } from './windGradient';
import { WindParticles } from './windParticles';

/** Speed colours are see-through, so the map stays readable underneath. */
const GRADIENT_OPACITY = 0.5;

/** Wind is loaded for the view plus this much of its size on every side, so panning finds it ready. */
const PADDING = 0.5;
/** Loads while the camera moves are at most this frequent, ms. */
const MOVE_LOAD_MS = 300;
/** "Now" and the data are refreshed this often while shown, ms. */
const REFRESH_MS = 5 * 60_000;

/**
 * The view (padded) and zoom. Longitudes aren't clamped to ±180°: around the antimeridian the map shows
 * longitudes beyond, and the wind follows them.
 */
function viewState(map: MapLibreMap): ViewState {
  const b = map.getBounds();
  const dx = (b.getEast() - b.getWest()) * PADDING;
  const dy = (b.getNorth() - b.getSouth()) * PADDING;
  const w = b.getWest() - dx;
  return {
    bounds: {
      w,
      // Never more than once around the world.
      e: Math.min(w + 360, b.getEast() + dx),
      s: Math.max(-85, b.getSouth() - dy),
      n: Math.min(85, b.getNorth() + dy),
    },
    zoom: map.getZoom(),
  };
}

/**
 * The forecast wind while `visible`: speed coloured as weather stations are, and particles drifting with it
 * (unless the user prefers reduced motion). Loads the wind for the view as the map moves.
 */
export function WindLayer({ visible }: { visible: boolean }) {
  const map = useMap();
  const field = useAtomValue(windFieldAtom);
  const load = useSetAtom(loadWindAtom);
  const cancel = useSetAtom(cancelWindAtom);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const gradient = useRef<WindGradient | null>(null);
  const particles = useRef<WindParticles | null>(null);

  useEffect(() => {
    if (!map) return;
    // Right above the anchor, in this order.
    const layers = map.getStyle().layers;
    const beforeId = layers[layers.findIndex((l) => l.id === WIND_ANCHOR) + 1]?.id;
    const g = new WindGradient(map, WIND_COLOR_STOPS, {
      id: WIND_GRADIENT,
      beforeId,
      opacity: GRADIENT_OPACITY,
    });
    const p = new WindParticles(map, { id: WIND_PARTICLES, beforeId });
    gradient.current = g;
    particles.current = p;
    return () => {
      p.dispose();
      g.dispose();
      gradient.current = particles.current = null;
    };
  }, [map]);

  useEffect(() => {
    gradient.current?.setRunning(visible);
    particles.current?.setRunning(visible && !reducedMotion);
    if (!visible) cancel();
  }, [map, visible, reducedMotion, cancel]);

  useEffect(() => {
    gradient.current?.setField(field);
    particles.current?.setField(field);
  }, [map, field]);

  /** Zoom (rounded) of the view last loaded. */
  const loadedZoom = useRef(0);

  /**
   * Loads the view's wind. While a gesture is still zooming, the levels models are shown at change as it
   * goes; loading each in-between level would waste requests, so that waits for the camera to stop.
   */
  const refresh = useEffectEvent((moving = false) => {
    if (!map) return;
    const view = viewState(map);
    const zoom = Math.round(view.zoom);
    if (moving && zoom !== loadedZoom.current) return;
    loadedZoom.current = zoom;
    void load(view);
  });

  useEffect(() => {
    if (!map || !visible) return;
    refresh();
    const timer = setInterval(() => document.visibilityState === 'visible' && refresh(), REFRESH_MS);
    // Load while the map moves too, so newly revealed areas fill in without waiting for it to stop.
    let moveTimer: ReturnType<typeof setTimeout> | undefined;
    const onMove = () => {
      moveTimer ??= setTimeout(() => {
        moveTimer = undefined;
        refresh(true);
      }, MOVE_LOAD_MS);
    };
    map.on('move', onMove);
    const onMoveEnd = () => refresh();
    map.on('moveend', onMoveEnd);
    return () => {
      clearInterval(timer);
      clearTimeout(moveTimer);
      map.off('move', onMove);
      map.off('moveend', onMoveEnd);
    };
  }, [map, visible]);

  return null;
}
